import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Tag, CheckCircle2, AlertCircle, Plus, Pencil } from "lucide-react";
import { adminApi } from "../api";
import PrecarioEditor from "../components/precarios/PrecarioEditor";
import PrecarioCreateModal from "../components/precarios/PrecarioCreateModal";

const EUR = (v) => (v == null ? "—" : Number(v).toLocaleString("pt-PT", { style: "currency", currency: "EUR" }));

function HeaderBar() {
  return (
    <>
      <Link to="/admin" className="inline-flex items-center gap-1 text-xs text-[#978D78] hover:text-[#0F1829] mb-3" data-testid="precarios-back">
        <ArrowLeft size={13} /> Voltar ao dashboard
      </Link>
      <div className="flex items-center gap-3 mb-5">
        <div className="w-11 h-11 grid place-items-center rounded-xl bg-[#0F1829] text-white"><Tag size={22} /></div>
        <div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: "'Fraunces', serif" }}>Preçários B2B</h1>
          <p className="text-sm text-[#4B5563]">Gestão de tabelas de preços versionadas por ano (cursos, palestras, workshops e consultoria).</p>
        </div>
      </div>
    </>
  );
}

function PrecariosList({ items, selectedId, onSelect, onActivate }) {
  return (
    <div className="bg-white border border-[#E6E1D6] rounded-md overflow-hidden mb-5" data-testid="precarios-list">
      <div className="px-4 py-2.5 border-b border-[#F3F0E7] bg-[#FAF8F2] flex items-center justify-between">
        <span className="text-[11px] uppercase tracking-[0.18em] text-[#978D78] font-bold">Preçários</span>
        <span className="text-xs text-[#6B7280]">{items.length} {items.length === 1 ? "preçário" : "preçários"}</span>
      </div>
      <div className="overflow-x-auto">
      <table className="w-full text-sm min-w-[640px]">
        <thead className="text-left text-[11px] uppercase tracking-[0.12em] text-[#978D78]">
          <tr className="border-b border-[#F3F0E7]">
            <th className="px-4 py-2">Ano</th>
            <th className="px-4 py-2">Nome</th>
            <th className="px-4 py-2">Vigência</th>
            <th className="px-4 py-2">IVA</th>
            <th className="px-4 py-2">Items</th>
            <th className="px-4 py-2">Estado</th>
            <th className="px-4 py-2 text-right">Acções</th>
          </tr>
        </thead>
        <tbody>
          {items.map((p) => {
            const isActive = p.Activo === "S";
            const isSelected = selectedId === p.ID_Precario;
            return (
              <tr key={p.ID_Precario} className={`border-b border-[#F3F0E7] last:border-0 ${isSelected ? "bg-[#FAF8F2]" : "hover:bg-[#FAF8F2]"}`} data-testid={`precario-row-${p.ID_Precario}`}>
                <td className="px-4 py-2.5 font-mono font-bold">{p.Ano}</td>
                <td className="px-4 py-2.5">{p.Nome}</td>
                <td className="px-4 py-2.5 text-xs text-[#6B7280] tabular-nums">{p.ValidoDe || "—"} → {p.ValidoAte || "—"}</td>
                <td className="px-4 py-2.5 tabular-nums">{Number(p.IVA_Pct || 0).toFixed(0)}%</td>
                <td className="px-4 py-2.5 tabular-nums">{p.NumItems}</td>
                <td className="px-4 py-2.5">
                  {isActive
                    ? <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wider bg-green-100 text-green-700 rounded"><CheckCircle2 size={12} />Activo</span>
                    : <span className="inline-flex items-center px-2 py-0.5 text-[11px] uppercase tracking-wider bg-[#F3F0E7] text-[#6B7280] rounded">Inactivo</span>
                  }
                </td>
                <td className="px-4 py-2.5 text-right space-x-2">
                  <button onClick={() => onSelect(p.ID_Precario)} className="text-xs px-2 py-1 border border-[#E6E1D6] rounded hover:bg-[#FAF8F2]" data-testid={`precario-edit-${p.ID_Precario}`}>
                    <Pencil size={12} className="inline mr-1" />Editar
                  </button>
                  {!isActive && (
                    <button onClick={() => onActivate(p.ID_Precario)} className="text-xs px-2 py-1 border border-green-200 bg-green-50 text-green-700 rounded hover:bg-green-100" data-testid={`precario-activate-${p.ID_Precario}`}>
                      Activar
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      </div>
    </div>
  );
}

export default function AdminPrecarios() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [info, setInfo] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const r = await adminApi.precarioList();
      setItems(r.items || []);
      // Auto-select active one if no selection
      if (!selectedId && r.items?.length) {
        const active = r.items.find((x) => x.Activo === "S") || r.items[0];
        setSelectedId(active.ID_Precario);
      }
    } catch (e) { setErr(e.message || "Erro a carregar preçários."); }
    finally { setLoading(false); }
  }, [selectedId]);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const onActivate = async (id) => {
    if (!window.confirm("Activar este preçário e desactivar os outros do mesmo ano?")) return;
    try {
      await adminApi.precarioActivate(id);
      setInfo("Preçário activado.");
      await load();
    } catch (e) { setErr(e.message); }
  };

  const onCreated = async (newId) => {
    setCreating(false);
    setInfo("Preçário criado.");
    setSelectedId(newId);
    await load();
  };

  return (
    <div data-testid="admin-precarios" className="max-w-7xl">
      <HeaderBar />

      {err && (
        <div className="mb-3 px-3 py-2 bg-red-50 border border-red-200 text-sm text-red-700 rounded flex items-start gap-2" data-testid="precarios-error">
          <AlertCircle size={14} className="mt-0.5 shrink-0" /> {err}
        </div>
      )}
      {info && (
        <div className="mb-3 px-3 py-2 bg-green-50 border border-green-200 text-sm text-green-700 rounded flex items-start gap-2" data-testid="precarios-info">
          <CheckCircle2 size={14} className="mt-0.5 shrink-0" /> {info}
        </div>
      )}

      <div className="flex items-center justify-between mb-3">
        <h2 className="text-[11px] uppercase tracking-[0.18em] text-[#978D78] font-bold">Versões</h2>
        <button onClick={() => setCreating(true)} className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-bold uppercase tracking-wider bg-[#0F1829] text-white rounded hover:bg-black" data-testid="precario-new-btn">
          <Plus size={13} /> Novo preçário
        </button>
      </div>

      {loading
        ? <p className="text-sm text-[#6B7280]">A carregar...</p>
        : <PrecariosList items={items} selectedId={selectedId} onSelect={setSelectedId} onActivate={onActivate} />
      }

      {selectedId && (
        <PrecarioEditor
          idPrecario={selectedId}
          onChanged={() => load()}
          onError={(m) => setErr(m)}
          onInfo={(m) => setInfo(m)}
          eur={EUR}
        />
      )}

      {creating && (
        <PrecarioCreateModal
          existingItems={items}
          onClose={() => setCreating(false)}
          onCreated={onCreated}
        />
      )}
    </div>
  );
}
